import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmailLoggingService } from "@/services/communication/EmailLoggingService";
import type { EmailFilters } from "@/types/email-logs";

export const useEmailLogs = (
  filters?: EmailFilters,
  page: number = 1,
  pageSize: number = 50,
) => {
  return useQuery({
    queryKey: ["email-logs", filters, page, pageSize],
    queryFn: () =>
      EmailLoggingService.getEmailLogs(
        filters,
        pageSize,
        (page - 1) * pageSize,
      ),
  });
};

export const useEmailLog = (id: string) => {
  return useQuery({
    queryKey: ["email-log", id],
    queryFn: () => EmailLoggingService.getEmailLog(id),
    enabled: !!id,
  });
};

export const useEmailStats = (filters?: EmailFilters) => {
  return useQuery({
    queryKey: ["email-stats", filters],
    queryFn: () => EmailLoggingService.getEmailStats(filters),
  });
};

export const useResendEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emailLogId: string) =>
      EmailLoggingService.resendEmail(emailLogId),
    onSuccess: () => {
      toast.success("Email resent successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to resend email: ${error.message}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["email-logs"] });
      queryClient.invalidateQueries({ queryKey: ["email-stats"] });
    },
  });
};
